import { PAGE_SECTIONS } from '../const'
import type { IElement, IPage } from '../types'

/** Minimum box size to trust DOM measurement (collapsed / transition frames). */
const MIN_LAYOUT_BOX = 8

/** Pixel geometry relative to the bound container (same rect math as drag-end, without anchor inference). */
export type FloatingLayoutGeometry = {
  top: number
  left: number
  bottom: number
  right: number
  height: number
  width: number
}

export function buildBoundContainerSelector(
  pageIndex: number,
  sectionType: typeof PAGE_SECTIONS.HEADERS | typeof PAGE_SECTIONS.FOOTERS | null
): string {
  if (sectionType === PAGE_SECTIONS.HEADERS) {
    return `.boundContainer-${pageIndex}-section-headers`
  }
  if (sectionType === PAGE_SECTIONS.FOOTERS) {
    return `.boundContainer-${pageIndex}-section-footers`
  }
  return `.boundContainer-${pageIndex}`
}

function escapeForAttributeSelector(id: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(id)
  }
  return id.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

/**
 * Measures the movable target's box relative to the page/section bound container.
 * Does not infer preferBottom/preferRight — those come from persisted styles.
 */
export function measureFloatingElementGeometryFromDom(
  elementId: string,
  pageIndex: number,
  sectionType: typeof PAGE_SECTIONS.HEADERS | typeof PAGE_SECTIONS.FOOTERS | null
): FloatingLayoutGeometry | null {
  if (typeof document === 'undefined') return null

  const containerSelector = buildBoundContainerSelector(pageIndex, sectionType)
  const container = document.querySelector(containerSelector) as HTMLElement | null
  if (!container) return null

  const safeId = escapeForAttributeSelector(elementId)
  const target = container.querySelector(`[data-element-id="${safeId}"]`) as HTMLElement | null
  if (!target) return null

  const targetRect = target.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()
  const height = targetRect.height
  const width = targetRect.width
  if (!Number.isFinite(height) || !Number.isFinite(width) || height < MIN_LAYOUT_BOX || width < MIN_LAYOUT_BOX) {
    return null
  }

  const snappedTop = targetRect.top - containerRect.top + (container.scrollTop || 0)
  const snappedLeft = targetRect.left - containerRect.left + (container.scrollLeft || 0)
  const snappedBottom = containerRect.height - (snappedTop + height)
  const snappedRight = containerRect.width - (snappedLeft + width)

  return {
    top: snappedTop < -1 ? 0 : snappedTop,
    left: snappedLeft < 0 ? 0 : snappedLeft,
    bottom: snappedBottom,
    right: snappedRight < 0 ? 0 : snappedRight,
    height,
    width,
  }
}

/** Draggable ids on this page whose persisted position uses bottom anchoring (`preferBottom: true`). */
export function collectBottomPreferDraggableIdsFromPage(page: IPage): string[] {
  const acc: string[] = []

  const visit = (elements?: IElement[]) => {
    if (!elements?.length) return
    for (const el of elements) {
      if (el?.component?.isDraggable && el.id && el.responsiveStyles?.large?.position?.preferBottom === true) {
        acc.push(el.id)
      }
      if (el.children?.length) {
        visit(el.children)
      }
    }
  }

  visit(page.children)
  visit(page.component?.options?.headers?.children)
  visit(page.component?.options?.footers?.children)

  return acc
}

export function findElementOnSinglePage(page: IPage, elementId: string): IElement | null {
  const walk = (nodes?: IElement[]): IElement | null => {
    if (!nodes?.length) return null
    for (const node of nodes) {
      if (node.id === elementId) return node
      const nested = walk(node.children)
      if (nested) return nested
    }
    return null
  }

  return walk(page.children) || walk(page.component?.options?.headers?.children) || walk(page.component?.options?.footers?.children) || null
}

/** Bound container for a draggable on this page: main content uses `null`. */
export function getFloatingBoundSectionForElementOnPage(
  page: IPage,
  elementId: string
): typeof PAGE_SECTIONS.HEADERS | typeof PAGE_SECTIONS.FOOTERS | null {
  const inSubtree = (nodes?: IElement[]): boolean => {
    if (!nodes?.length) return false
    for (const n of nodes) {
      if (n.id === elementId) return true
      if (inSubtree(n.children)) return true
    }
    return false
  }

  if (inSubtree(page.component?.options?.headers?.children)) {
    return PAGE_SECTIONS.HEADERS
  }
  if (inSubtree(page.component?.options?.footers?.children)) {
    return PAGE_SECTIONS.FOOTERS
  }
  return null
}

/** Larger epsilon for layout resync to avoid subpixel / font reflow churn. */
const BOTTOM_PREFER_RESYNC_EPS = 1.25

type PositionLike = {
  top?: number
  left?: number
  bottom?: number
  right?: number
  preferRight?: boolean
}

function num(v: unknown): number {
  if (typeof v === 'number') return v
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function readPositiveDim(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v) && v >= MIN_LAYOUT_BOX) return v
  if (typeof v === 'string') {
    const n = parseFloat(v)
    if (Number.isFinite(n) && n >= MIN_LAYOUT_BOX) return n
  }
  return 0
}

/**
 * Recompute top/left/right from stored bottom anchor + dimensions when sibling layout
 * changes the bound container height. Avoids reading the movable target during editor
 * reflow (zero-height / stale frames) which could write a bogus `bottom` and hide the field.
 */
export function computeBottomPreferGeometryFromStoredLayout(
  element: IElement,
  pageIndex: number,
  sectionType: typeof PAGE_SECTIONS.HEADERS | typeof PAGE_SECTIONS.FOOTERS | null
): FloatingLayoutGeometry | null {
  if (typeof document === 'undefined') return null

  const pos = element.responsiveStyles?.large?.position
  if (!pos?.preferBottom || !element.id) return null

  const container = document.querySelector(buildBoundContainerSelector(pageIndex, sectionType)) as HTMLElement | null
  if (!container) return null

  const rect = container.getBoundingClientRect()
  const ch = rect.height
  const cw = rect.width
  if (!Number.isFinite(ch) || ch <= 0 || !Number.isFinite(cw) || cw <= 0) return null

  let height = readPositiveDim(element.responsiveStyles?.large?.dimensions?.height)
  let width = readPositiveDim(element.responsiveStyles?.large?.dimensions?.width)

  if (height < MIN_LAYOUT_BOX || width < MIN_LAYOUT_BOX) {
    const dom = measureFloatingElementGeometryFromDom(element.id, pageIndex, sectionType)
    if (!dom) return null
    if (height < MIN_LAYOUT_BOX) height = dom.height
    if (width < MIN_LAYOUT_BOX) width = dom.width
  }
  if (height < MIN_LAYOUT_BOX || width < MIN_LAYOUT_BOX) return null

  const bottom = num(pos.bottom)
  const preferRight = !!pos.preferRight

  let top = ch - bottom - height
  let left: number
  let right: number

  if (preferRight) {
    right = num(pos.right)
    left = cw - right - width
  } else {
    left = num(pos.left)
    right = cw - left - width
  }

  if (top < -1) top = 0
  if (left < 0) left = 0
  if (right < 0) right = 0

  if (!Number.isFinite(top) || !Number.isFinite(left) || !Number.isFinite(right) || !Number.isFinite(bottom)) {
    return null
  }

  if (top > ch + 200 || left > cw + 200) {
    return null
  }

  return {
    top,
    left,
    bottom,
    right,
    height,
    width,
  }
}

/**
 * Whether persisted position differs from measured geometry enough to write.
 * Only used for elements that already have `preferBottom: true` in the store.
 */
export function bottomPreferLayoutNeedsWrite(stored: PositionLike | undefined, measured: FloatingLayoutGeometry): boolean {
  if (!stored) return true
  if (Math.abs(num(stored.bottom) - measured.bottom) >= BOTTOM_PREFER_RESYNC_EPS) return true
  if (stored.preferRight) {
    if (Math.abs(num(stored.right) - measured.right) >= BOTTOM_PREFER_RESYNC_EPS) return true
  } else {
    if (Math.abs(num(stored.left) - measured.left) >= BOTTOM_PREFER_RESYNC_EPS) return true
  }
  if (Math.abs(num(stored.top) - measured.top) >= BOTTOM_PREFER_RESYNC_EPS) return true
  return false
}

/**
 * Writes top/left/bottom/right for a bottom-anchored field after sibling layout changes.
 * Keeps `preferBottom: true`, preserves `preferRight`. Does not touch dimensions (avoids feedback loops).
 */
export function applyBottomPreferPositionSync(element: IElement, geometry: FloatingLayoutGeometry, preferRight: boolean): void {
  if (!element.responsiveStyles) {
    element.responsiveStyles = { large: {} } as IElement['responsiveStyles']
  }
  if (!element.responsiveStyles.large) {
    element.responsiveStyles.large = {} as IElement['responsiveStyles']['large']
  }
  const large = element.responsiveStyles.large
  large.position = {
    top: geometry.top,
    left: geometry.left,
    bottom: geometry.bottom,
    right: geometry.right,
    preferBottom: true,
    preferRight,
  }
}
